import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import loaderimage from "../../images/giphy.gif";
import ConfirmationModal from "../shared/ConfirmationModal";
import AllFailureModal from "../shared/AllFailureModal";
import NewconfirmationModal from "../shared/NewconfirmationModal";
import TeamMemberDetailsModal from "../shared/TeamMemberDetailsModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface TeamMember {
  id: number;
  name: string;
  designation: string;
  department: string;
  image: string;
  description: string;
  sequence: number;
  linkedin: string;
  portfolio: string;
  twitter: string;
}

const OurTeam = () => {
  const [teamList, setTeamList] = useState<TeamMember[]>([]);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [filteredTeam, setFilteredTeam] = useState<TeamMember[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalTeamMembers, setTotalTeamMembers] = useState(0);
  const [pageSize] = useState(10); 

  const fetchTeamMembers = async (page = 1) => { 
    try {
      setLoader(true);
      setError(null);
      
      const url = `${process.env.REACT_APP_API_URL}ourteam/allourteam?page=${page}&limit=${pageSize}`;
      const response = await axios.get(url);
      setTeamList(response.data.teamMembers || response.data); 
      setFilteredTeam(response.data.teamMembers || response.data); 
      setCurrentPage(response.data.currentPage || page);
      setTotalPages(response.data.totalPages || 1);
      setTotalTeamMembers(response.data.totalTeamMembers || (response.data.length || 0));
    } catch (error: any) {
      setError(error.message || "Failed to fetch team member.");
      console.error("Error fetching team member:", error);
      toast.error("Failed to load team member");
    } finally {
      setLoader(false);
    }
  };
  useEffect(() => {
    fetchTeamMembers(); 
  }, []);
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchTerm === '') {
        setFilteredTeam(teamList);
      } else {
        const filtered = teamList.filter((member) =>
          member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          member.department.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredTeam(filtered);
      }
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, teamList]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const deleteTeamMember = async (id: number) => {
    try {
      const url = (`${process.env.REACT_APP_API_URL}ourteam/removeteammate/${id}`);
      const token = localStorage.getItem("jwttoken") || "";
      await axios.put(url, {},
        {
          headers: { "x-access-token": token }
        });
      setShowSuccessModal(true);
      fetchTeamMembers(currentPage); 
    } catch (err: any) {
      console.error("Error deleting team member:", err);
      setFailureMessage(err.message || "Failed to delete team member.");
      setShowFailureModal(true);
    }
  };

  const handleDeleteClick = (id: number) => {
    setSelectedMemberId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = (confirmed: boolean) => {
    if (confirmed && selectedMemberId !== null) {
      deleteTeamMember(selectedMemberId);
    }
    setShowDeleteModal(false);
    setSelectedMemberId(null);
  };
  const handleViewDetails = (member: TeamMember) => {
    setSelectedTeamMember(member);
    setShowDetailsModal(true);
  };
 const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchTeamMembers(page);
  };

  // Calculate the start and end entry for the current page
  const startEntry = (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalTeamMembers);

  const wrappedCellStyle = {
    maxWidth: '150px',
    wordBreak: 'break-word' as const,
    whiteSpace: 'normal' as const
  };
  const linkStyle = {
    color: '#0066cc',
    textDecoration: 'underline',
    cursor: 'pointer'
  };

  return (
    <>
      <div className="">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-black fw-bold">Team Management</h2>
          <div className="d-flex gap-2 align-items-center" style={{ width: "510px" }}>
            <span className="fw-bold text-secondary text-nowrap">{filteredTeam.length} results</span>
            <input
              type="text"
              className="form-control"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearch}
            />
            <Link to="/ourteamadd" className="btn bg-info text-white">
              + Add Member
            </Link>
          </div>
        </div>

        <div className="card shadow-sm border-0 rounded">
          <div className="card-header bg-info border-bottom py-3">
            <h5 className="mb-0 text-white">Team Members List</h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light text-center">
                  <tr>
                    <th>#</th>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Department</th>
                    <th>Description</th>
                    <th>Sequence</th>
                    <th>Social Links</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan={10} className="text-center py-2">
                        <img src={loaderimage} alt="Loading..." height="40" />
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={10} className="text-center text-danger fw-bold py-3">
                        <div className="alert alert-danger text-center m-3" role="alert">
                          Oops! Something went wrong. Please try again later.
                        </div>
                      </td>
                    </tr>
                  ) : filteredTeam.length > 0 ? (
                    filteredTeam.map((member, index) => (
                      <tr key={member.id}>
                        <td className="text-center">{(currentPage - 1) * pageSize + index + 1}</td> {/* Modified index */}
                        <td className="text-center" style={{ width: "70px" }}>
                          <div className="rounded-circle border d-flex align-items-center justify-content-center mx-auto bg-light" style={{ width: "50px", height: "50px", overflow: "hidden" }} >
                            <img
                              src={`${process.env.REACT_APP_API_URL}${member.image}`}
                              alt={member.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover"
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50' viewBox='0 0 24 24'%3E%3Cpath fill='%23999' d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";
                              }}
                            />
                          </div>
                        </td>
                        <td>{member.name}</td>
                        <td>{member.designation}</td>
                        <td>{member.department}</td>
                        <td style={{ maxWidth: "200px" }}>
                          <div className="text-truncate" title={member.description}>
                            {member.description}
                          </div>
                        </td>
                        <td className="text-center">{member.sequence}</td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            {member.linkedin && (
                              <a href={member.linkedin}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary">
                                LinkedIn
                              </a>
                            )}
                            {member.portfolio && (
                              <a href={member.portfolio}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-success">
                                Portfolio
                              </a>
                            )}
                            {member.twitter && (
                              <a href={member.twitter}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-info">
                                Twitter
                              </a>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              onClick={() => handleViewDetails(member)}
                              className="btn btn-sm btn-outline-primary"
                            >
                              View
                            </button>
                            <Link to={`/ourteamedit/${member.id}`} className="btn btn-sm btn-outline-warning">
                              Edit
                            </Link>
                            <button
                              onClick={() => handleDeleteClick(member.id)}
                              className="btn btn-sm btn-outline-danger"
                            >
                              Delete
                            </button>
                          </div>

                        </td>
                      </tr>
                    ))) : (
                    <tr>
                      <td colSpan={10} className="text-center  text-muted fw-bold">
                        <div className="alert alert-secondary text-center m-3" role="alert">
                          No Team Members available
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="position-sticky bottom-0 bg-white py-2 border-top">
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted ps-3">
                  Showing {startEntry}-{endEntry} of {totalTeamMembers} entries
                </div>
                <nav>
                  <ul className="pagination mb-0 pe-3">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, index) => (
                      <li
                        key={index}
                        className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>

        <TeamMemberDetailsModal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          member={selectedTeamMember}
        />

        <ConfirmationModal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          modalMessage="Are you sure you want to delete this team member?"
          confirmation={handleDeleteConfirm}
          operationType="Delete"
        />
        <NewconfirmationModal
          show={showSuccessModal}
          onHide={() => setShowSuccessModal(false)}
          modalMessage="Team member deleted successfully."
          operationType="Ok"
        />
      </div>
    </>
  );
};

export default OurTeam;

